import React, { useState, useEffect } from 'react';
import { getAuth, updatePassword, reauthenticateWithCredential, EmailAuthProvider, updateEmail } from 'firebase/auth';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Phone, Lock, Camera, ArrowLeft, Home, FileText, Shield, Briefcase, MapPin, Calendar, CheckCircle, Palette, Sun, Moon, Monitor } from 'lucide-react';
import { db, storage } from '../firebase';
import { useTheme } from '../contexts/ThemeContext';

const tabs = [
  { id: 'info', label: 'Infos', longLabel: 'Informations personnelles', icon: User },
  { id: 'security', label: 'Sécurité', longLabel: 'Sécurité', icon: Shield },
  { id: 'professional', label: 'Pro', longLabel: 'Informations professionnelles', icon: Briefcase },
  { id: 'appearance', label: 'Apparence', longLabel: 'Apparence', icon: Palette }
];

const themeOptions = [
  { id: 'system', label: 'Système', description: 'Suivre les préférences système', icon: Monitor },
  { id: 'light', label: 'Clair', description: 'Toujours utiliser le mode clair', icon: Sun },
  { id: 'dark', label: 'Sombre', description: 'Toujours utiliser le mode sombre', icon: Moon }
];

const ProfilePage = () => {
  const [activeTab, setActiveTab] = useState('info');
  const [userData, setUserData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { theme, changeTheme } = useTheme();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // États pour les informations personnelles et professionnelles
  const [address, setAddress] = useState({
    street: '',
    city: '',
    postalCode: '',
    country: 'France',
    birthDate: ''  // Ajout de la date de naissance
  });
  const [socialSecurity, setSocialSecurity] = useState('');
  const [professionalInfo, setProfessionalInfo] = useState({
    contractType: '',
    startDate: '',
    cesuNumber: '',
    nanCrcesu: '',
    bankInfo: {
      iban: '',
      bic: ''
    }
  });

  useEffect(() => {
    const fetchUserData = async () => {
      setIsLoading(true);
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'employees', user.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            setUserData({ id: userDoc.id, ...data });
            setNewEmail(user.email);
            setAddress(data.address || {
              street: '',
              city: '',
              postalCode: '',
              country: 'France'
            });
            setSocialSecurity(data.socialSecurity || '');
            setProfessionalInfo(data.professionalInfo || {
              contractType: '',
              startDate: '',
              cesuNumber: '',
              nanCrcesu: '',
              bankInfo: {
                iban: '',
                bic: ''
              }
            });
          } else {
            setError("Données utilisateur non trouvées");
          }
        } catch (err) {
          setError("Erreur lors du chargement des données utilisateur");
          console.error(err);
        }
      } else {
        navigate('/login');
      }
      setIsLoading(false);
    };

    fetchUserData();
  }, [navigate]);

  const handleInputChange = (e, section = 'main') => {
    const { name, value } = e.target;
    switch (section) {
      case 'address':
        setAddress(prev => ({ ...prev, [name]: value }));
        break;
      case 'professional':
        if (name.includes('iban') || name.includes('bic')) {
          setProfessionalInfo(prev => ({
            ...prev,
            bankInfo: {
              ...prev.bankInfo,
              [name]: value
            }
          }));
        } else if (name === 'cesuNumber' || name === 'nanCrcesu') {
          setProfessionalInfo(prev => ({ ...prev, [name]: value }));
        } else {
          setProfessionalInfo(prev => ({ ...prev, [name]: value }));
        }
        break;
      default:
        setUserData(prev => ({ ...prev, [name]: value }));
    }
  };

  const validateSocialSecurity = (number) => {
    const cleanNumber = number.replace(/\s/g, '');
    const regex = /^[12][0-9]{2}(0[1-9]|1[0-2])(2[AB]|[0-9]{2})[0-9]{8}$/;
    return regex.test(cleanNumber);
  };

  const formatSocialSecurity = (number) => {
    if (!number) return '';
    const cleanNumber = number.replace(/\s/g, '');
    return cleanNumber.replace(/(\d{1})(\d{2})(\d{2})(\d{2})(\d{3})(\d{3})(\d{2})/, '$1 $2 $3 $4 $5 $6 $7');
  };

  const handleSave = async (section) => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    if (section === 'professional' && socialSecurity && !validateSocialSecurity(socialSecurity)) {
      setError("Le numéro de sécurité sociale n'est pas valide");
      setIsLoading(false);
      return;
    }

    try {
      const auth = getAuth();
      const user = auth.currentUser;
      const updateData = {};

      switch (section) {
        case 'personal':
          updateData.firstName = userData.firstName;
          updateData.lastName = userData.lastName;
          updateData.phoneNumber = userData.phoneNumber;
          updateData.birthDate = userData.birthDate; // Ajout de la date de naissance
          updateData.address = address;
          break;
        case 'professional':
          updateData.socialSecurity = socialSecurity;
          updateData.professionalInfo = professionalInfo;
          break;
      }

      await updateDoc(doc(db, 'employees', user.uid), updateData);
      setSuccess("Profil mis à jour avec succès");
      setIsEditing(false);
    } catch (err) {
      setError("Erreur lors de la mise à jour du profil");
      console.error(err);
    }
    setIsLoading(false);
  };

  const handleThemeChange = async (newTheme) => {
    try {
      await changeTheme(newTheme);
      setSuccess("Préférence de thème mise à jour avec succès");
    } catch (err) {
      setError("Erreur lors de la mise à jour du thème");
      console.error(err);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setIsLoading(true);
      setError(null);
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        const storageRef = ref(storage, `profilePhotos/${user.uid}`);
        await uploadBytes(storageRef, file);
        const photoURL = await getDownloadURL(storageRef);
        await updateDoc(doc(db, 'employees', user.uid), { photoURL });
        setUserData(prev => ({ ...prev, photoURL }));
        setSuccess("Photo de profil mise à jour avec succès");
      } catch (err) {
        setError("Erreur lors de la mise à jour de la photo");
        console.error(err);
      }
      setIsLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      setSuccess("Mot de passe mis à jour avec succès");
      setNewPassword('');
      setCurrentPassword('');
    } catch (err) {
      setError("Erreur lors de la mise à jour du mot de passe");
      console.error(err);
    }
    setIsLoading(false);
  };

  const handleEmailChange = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updateEmail(user, newEmail);
      await updateDoc(doc(db, 'employees', user.uid), { email: newEmail });
      setSuccess("Email mis à jour avec succès");
      setCurrentPassword('');
    } catch (err) {
      setError("Erreur lors de la mise à jour de l'email");
      console.error(err);
    }
    setIsLoading(false);
  };

  const renderPersonalInfo = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Prénom</label>
          <input
            type="text"
            name="firstName"
            value={userData?.firstName || ''}
            onChange={handleInputChange}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-300 dark:focus:border-indigo-500 focus:ring focus:ring-indigo-200 dark:focus:ring-indigo-800 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Nom</label>
          <input
            type="text"
            name="lastName"
            value={userData?.lastName || ''}
            onChange={handleInputChange}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-300 dark:focus:border-indigo-500 focus:ring focus:ring-indigo-200 dark:focus:ring-indigo-800 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Date de naissance</label>
          <input
            type="date"
            name="birthDate"
            value={userData?.birthDate || ''}
            onChange={handleInputChange}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-300 dark:focus:border-indigo-500 focus:ring focus:ring-indigo-200 dark:focus:ring-indigo-800 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Téléphone</label>
          <input
            type="tel"
            name="phoneNumber"
            value={userData?.phoneNumber || ''}
            onChange={handleInputChange}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-300 dark:focus:border-indigo-500 focus:ring focus:ring-indigo-200 dark:focus:ring-indigo-800 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Rue</label>
          <input
            type="text"
            name="street"
            value={address.street}
            onChange={(e) => handleInputChange(e, 'address')}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-300 dark:focus:border-indigo-500 focus:ring focus:ring-indigo-200 dark:focus:ring-indigo-800 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Ville</label>
          <input
            type="text"
            name="city"
            value={address.city}
            onChange={(e) => handleInputChange(e, 'address')}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-300 dark:focus:border-indigo-500 focus:ring focus:ring-indigo-200 dark:focus:ring-indigo-800 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Code Postal</label>
          <input
            type="text"
            name="postalCode"
            value={address.postalCode}
            onChange={(e) => handleInputChange(e, 'address')}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-300 dark:focus:border-indigo-500 focus:ring focus:ring-indigo-200 dark:focus:ring-indigo-800 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>
      </div>
      <div className="flex justify-end">
        <button
          onClick={() => handleSave('personal')}
          disabled={isLoading}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded transition-colors"
        >
          {isLoading ? 'Enregistrement...' : 'Enregistrer'}
        </button>
      </div>
    </div>
  );

  const renderSecurity = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg p-6">
        <h3 className="text-lg font-medium mb-4 text-gray-900 dark:text-white">Changer l'email</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Nouvel email</label>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-300 dark:focus:border-indigo-500 focus:ring focus:ring-indigo-200 dark:focus:ring-indigo-800 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Mot de passe actuel</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-300 dark:focus:border-indigo-500 focus:ring focus:ring-indigo-200 dark:focus:ring-indigo-800 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <button
            onClick={handleEmailChange}
            disabled={isLoading}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded transition-colors"
          >
            Mettre à jour l'email
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg p-6">
        <h3 className="text-lg font-medium mb-4 text-gray-900 dark:text-white">Changer le mot de passe</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Mot de passe actuel</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-300 dark:focus:border-indigo-500 focus:ring focus:ring-indigo-200 dark:focus:ring-indigo-800 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Nouveau mot de passe</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-300 dark:focus:border-indigo-500 focus:ring focus:ring-indigo-200 dark:focus:ring-indigo-800 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <button
            onClick={handlePasswordChange}
            disabled={isLoading}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded transition-colors"
          >
            Changer le mot de passe
          </button>
        </div>
      </div>
    </div>
  );

  const renderProfessionalInfo = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Numéro de sécurité sociale</label>
          <input
            type="text"
            value={socialSecurity}
            onChange={(e) => setSocialSecurity(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-300 dark:focus:border-indigo-500 focus:ring focus:ring-indigo-200 dark:focus:ring-indigo-800 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="X XX XX XX XXX XXX XX"
            maxLength="15"
          />
        </div>
  
        <div className="col-span-2 md:col-span-1">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Type de contrat</label>
          <div className="mt-1 p-2 w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300">
            {userData?.professionalInfo?.contractType || 'Non spécifié'}
          </div>
        </div>
  
        <div className="col-span-2 md:col-span-1">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Date de début</label>
          <div className="mt-1 p-2 w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300">
            {userData?.professionalInfo?.startDate ?
              new Date(userData.professionalInfo.startDate).toLocaleDateString('fr-FR') :
              'Non spécifiée'
            }
          </div>
        </div>

        {userData?.professionalInfo?.contractType === 'Cesu' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Numéro CESU</label>
              <input
                type="text"
                name="cesuNumber"
                value={professionalInfo.cesuNumber || ''}
                onChange={(e) => handleInputChange(e, 'professional')}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-300 dark:focus:border-indigo-500 focus:ring focus:ring-indigo-200 dark:focus:ring-indigo-800 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Ex: 123456789"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Code NAN CRCESU</label>
              <input
                type="text"
                name="nanCrcesu"
                value={professionalInfo.nanCrcesu || ''}
                onChange={(e) => handleInputChange(e, 'professional')}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-300 dark:focus:border-indigo-500 focus:ring focus:ring-indigo-200 dark:focus:ring-indigo-800 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Ex: SAP123456789"
              />
              {!professionalInfo.nanCrcesu && (
                <p className="mt-2 text-sm text-blue-600 dark:text-blue-400">
                  ℹ️ Pour obtenir votre code NAN, rendez-vous sur{' '}
                  <a
                    href="https://www.cr-cesu.fr/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium underline hover:text-blue-800 dark:hover:text-blue-300"
                  >
                    www.cr-cesu.fr
                  </a>
                </p>
              )}
            </div>
          </>
        )}

        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Informations bancaires</label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600 dark:text-gray-400">IBAN</label>
              <input
                type="text"
                name="iban"
                value={professionalInfo.bankInfo?.iban || ''}
                onChange={(e) => handleInputChange(e, 'professional')}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-300 dark:focus:border-indigo-500 focus:ring focus:ring-indigo-200 dark:focus:ring-indigo-800 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 dark:text-gray-400">BIC</label>
              <input
                type="text"
                name="bic"
                value={professionalInfo.bankInfo?.bic || ''}
                onChange={(e) => handleInputChange(e, 'professional')}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-300 dark:focus:border-indigo-500 focus:ring focus:ring-indigo-200 dark:focus:ring-indigo-800 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="XXXXXXPPXXX"
              />
            </div>
          </div>
        </div>
      </div>
  
      <div className="flex justify-end">
        <button
          onClick={() => handleSave('professional')}
          disabled={isLoading}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded transition-colors"
        >
          {isLoading ? 'Enregistrement...' : 'Enregistrer'}
        </button>
      </div>
    </div>
  );

  const renderAppearance = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg p-6">
        <h3 className="text-lg font-medium mb-4 text-gray-900 dark:text-white">Préférence de thème</h3>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Choisissez votre thème préféré. Le thème sera appliqué immédiatement et sauvegardé pour vos prochaines connexions.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {themeOptions.map((option) => (
            <button
              key={option.id}
              onClick={() => handleThemeChange(option.id)}
              className={`p-4 border-2 rounded-lg text-left transition-all ${
                theme === option.id
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <div className="flex items-center space-x-3 mb-2">
                <option.icon 
                  size={24} 
                  className={
                    theme === option.id ? 'text-blue-500 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'
                  } 
                />
                <span className={`font-medium ${
                  theme === option.id ? 'text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300'
                }`}>
                  {option.label}
                </span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {option.description}
              </p>
              {theme === option.id && (
                <div className="mt-2 flex items-center text-blue-600 dark:text-blue-400 text-sm">
                  <CheckCircle size={16} className="mr-1" />
                  Sélectionné
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  if (isLoading && !userData) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="w-full bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="max-w-4xl mx-auto p-4">
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={() => navigate(-1)} 
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700">
            <ArrowLeft size={24} className="text-gray-700 dark:text-gray-300" />
          </button>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">Mon Profil</h1>
          <div className="w-10"></div>
        </div>

      {userData && (
        <>
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 md:p-6 mb-4 md:mb-6">
            <div className="flex flex-col md:flex-row items-center">
              <div className="relative mb-4 md:mb-0">
                {userData.photoURL ? (
                  <img 
                    src={userData.photoURL} 
                    alt="Profile" 
                    className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                    <User size={40} className="text-gray-400 dark:text-gray-500" />
                  </div>
                )}
                <label className="absolute bottom-0 right-0 bg-blue-500 rounded-full p-2 cursor-pointer hover:bg-blue-600 transition-colors">
                  <Camera className="w-4 h-4 text-white" />
                  <input 
                    type="file" 
                    className="hidden" 
                    accept="image/*" 
                    onChange={handlePhotoUpload} 
                  />
                </label>
              </div>
              <div className="text-center md:text-left md:ml-6">
                <h2 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-white">
                  {userData.firstName} {userData.lastName}
                </h2>
                <p className="text-gray-600 dark:text-gray-400 text-sm md:text-base">{userData.email}</p>
              </div>
            </div>
          </div>

          <div className="flex overflow-x-auto space-x-1 mb-4 md:mb-6 bg-gray-100 dark:bg-gray-700 p-1 rounded-lg">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-3 md:px-4 py-2 rounded-md whitespace-nowrap flex-1 transition-colors
                         ${activeTab === tab.id 
                           ? 'bg-white dark:bg-gray-600 shadow text-blue-500 dark:text-blue-400' 
                           : 'hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'}`}
              >
                <tab.icon size={18} className={activeTab === tab.id ? 'text-blue-500 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'} />
                <span className="text-sm md:text-base">
                  {isMobile ? tab.label : tab.longLabel}
                </span>
              </button>
            ))}
          </div>

          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 md:p-6">
            {activeTab === 'info' && renderPersonalInfo()}
            {activeTab === 'security' && renderSecurity()}
            {activeTab === 'professional' && renderProfessionalInfo()}
            {activeTab === 'appearance' && renderAppearance()}
          </div>
        </>
      )}

      {error && (
        <div className="fixed bottom-4 right-4 bg-red-100 dark:bg-red-900 border-l-4 border-red-500 dark:border-red-400 text-red-700 dark:text-red-300 p-4 rounded shadow-lg">
          {error}
        </div>
      )}
      
      {success && (
        <div className="fixed bottom-4 right-4 bg-green-100 dark:bg-green-900 border-l-4 border-green-500 dark:border-green-400 text-green-700 dark:text-green-300 p-4 rounded shadow-lg">
          {success}
        </div>
      )}
      </div>
    </div>
  );
};

export default ProfilePage;
